import { useSearchParams, useNavigate } from "react-router-dom"
import { useState } from "react"
import { toast } from "react-toastify"
import api from "../../../../services/axiosInstance"
import Field from "../../../auth/components/Field"
import { Lock, Eye, EyeOff, ArrowRight, ShieldCheck, Fingerprint } from "lucide-react"

 const getStrength = (pwd: string, isMatch: boolean) => {
  if (isMatch && pwd.length >= 6) return { level: 3, label: "Excellent", color: "#10b981" }
  if (pwd.length >= 6)            return { level: 2, label: "Correct",   color: "#0F52BA" }
  if (pwd.length > 0)             return { level: 1, label: "Trop court", color: "#f59e0b" }
  return null
}

export default function ConfigPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  
  const token = params.get("token")


  const isValidPassword = (pwd: string) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(pwd)
  const [password,            setPassword]            = useState("")
  const [confirmPassword,     setConfirmPassword]     = useState("")
  const [showPassword,        setShowPassword]        = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading,           setIsLoading]           = useState(false)

  const isMatch      = password === confirmPassword && password !== ""
  const isLongEnough = password.length >= 6
const isValid = isValidPassword(password) && isMatch
  const strength     = getStrength(password, isMatch)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    setIsLoading(true)
    try {
      await api.post("/api/users/register", { token, password, confirmPassword })
      toast.success("Compte activé  ")
      if (!token) {
  toast.error("Lien invalide ou expiré")
  return
}
      setTimeout(() => navigate("/auth"), 1000)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erreur serveur")
    } finally {
      setIsLoading(false)
    }
  }

   const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
    <button
      type="button"
      onClick={onToggle}
      className="p-2 rounded-lg text-slate-400 hover:text-[#0F52BA] hover:bg-blue-50 transition-colors duration-150"
      tabIndex={0}
    >
      {show ? <EyeOff size={16} strokeWidth={1.8} /> : <Eye size={16} strokeWidth={1.8} />}
    </button>
  )

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 px-4">
    <div className="w-full max-w-sm">
    <div className="relative bg-white rounded-2xl border border-slate-200/70 shadow-[0_8px_32px_-8px_rgba(15,82,186,0.10)] overflow-hidden">

       <div className="h-[3px] w-full bg-[#0F52BA]" />

      <div className="px-8 py-10 sm:px-10">

         <div className="flex items-start gap-4 mb-8">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Fingerprint size={22} strokeWidth={1.6} className="text-[#0F52BA]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 tracking-tight leading-snug">
              Sécurisez votre accès
            </h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Dernière étape pour activer votre compte
            </p>
          </div>
        </div>

         <form onSubmit={handleSubmit} className="space-y-4">

          <Field
            id="password"
            type={showPassword ? "text" : "password"}
            label="Nouveau mot de passe"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            icon={
              <Lock
                size={15}
                strokeWidth={1.8}
                className={isLongEnough ? "text-[#0F52BA]" : "text-slate-400"}
              />
            }
            extra={<EyeToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />}
          />

          <Field
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            label="Confirmer le mot de passe"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="••••••••"
            icon={
              <ShieldCheck
                size={15}
                strokeWidth={1.8}
                className={isMatch ? "text-emerald-500" : "text-slate-400"}
              />
            }
            
            extra={<EyeToggle show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />}
          />
<p className="text-xs text-slate-400">
  8 caractères minimum, avec majuscule, minuscule, chiffre et symbole
</p>
           <div className="pt-1 min-h-[40px]">
            {strength && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                    Sécurité
                  </span>
                  <span
                    className="text-[11px] font-semibold uppercase tracking-wider transition-colors duration-300"
                    style={{ color: strength.color }}
                  >
                    {strength.label}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {[1, 2, 3].map((step) => (
                    <div
                      key={step}
                      className="h-1 flex-1 rounded-full transition-all duration-400"
                      style={{
                        backgroundColor: step <= strength.level ? strength.color : "#e2e8f0",
                        opacity: step <= strength.level ? 1 : 1,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

           <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`
              group relative w-full flex items-center justify-center gap-2.5
              px-6 py-3.5 rounded-xl text-sm font-semibold
              transition-all duration-200 mt-2
              ${isValid && !isLoading
                ? "bg-[#0F52BA] text-white hover:bg-[#0d48a8] active:scale-[0.98] shadow-sm shadow-blue-200/60"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }
            `}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <span>Activer mon compte</span>
                <ArrowRight
                  size={16}
                  strokeWidth={2}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </>
            )}
          </button>

        </form>

         <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          </div>
          <p className="text-[11px] text-slate-400">
            Connexion sécurisée — vos données sont chiffrées
          </p>
        </div>

      </div>
    </div>
    </div>
    </div>
  )
}
